import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import * as E from '@react-email/components'
import { json, redirect, type ActionFunctionArgs, type MetaFunction, type LinksFunction } from '@remix-run/node'
import { useFetcher } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { Button } from '#app/components/buttons/button.tsx'
import { Flex } from '#app/components/layout/flex.tsx'
import { A } from '#app/components/typography/a.tsx'
import { H1 } from '#app/components/typography/h1.tsx'
import { P } from '#app/components/typography/p.tsx'
import { Input } from '#app/old/components/data-entry/input.tsx'
import { Label } from '#app/old/components/data-entry/label.tsx'
import { GeneralErrorBoundary } from '#app/old/components/error-boundary.tsx'
import { CenteredFormContainer } from '#app/old/patterns/centered-form-container.tsx'
import { FormField } from '#app/old/patterns/form-field.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { mergeMeta } from '#app/utils/seo.ts'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { prepareVerification } from './verify.server.ts'

const ForgotPasswordSchema = z.object({
	email: EmailSchema,
})

export const links: LinksFunction = () => {
	return [{ rel: 'canonical', href: `https://verveui.pro/forgot-password` }]
}

export const meta: MetaFunction = mergeMeta(() => [{ title: 'Password Recovery for VerveUI' }])

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = parseWithZod(formData, { schema: ForgotPasswordSchema })
	if (submission.status !== 'success') {
		return json(submission.reply(), { status: submission.status === 'error' ? 400 : 200 })
	}
	const { email } = submission.value

	const user = await prisma.user.findFirstOrThrow({ where: { email }, select: { email: true } })
	if (!user) {
		return json(submission.reply({ formErrors: ['No user exists with this email.'] }))
	}

	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'reset-password',
		target: email,
	})

	const response = await sendEmail({
		to: user.email,
		subject: `VerveUI Password Reset`,
		react: <ForgotPasswordEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
	})
	if (response.status !== 'success') {
		return json(submission.reply({ formErrors: ['Could not send email. Please try again.'] }))
	}
	return redirect(redirectTo.toString())
}

function ForgotPasswordEmail({ onboardingUrl, otp }: { onboardingUrl: string; otp: string }) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>VerveUI Password Reset</E.Text>
				</h1>
				<p>
					<E.Text>
						Here's your verification code: <strong>{otp}</strong>
					</E.Text>
				</p>
				<p>
					<E.Text>Or click the link:</E.Text>
				</p>
				<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
			</E.Container>
		</E.Html>
	)
}

export default function ForgotPasswordRoute() {
	const forgotPassword = useFetcher<typeof action>()

	const [form, { email }] = useForm({
		id: 'forgot-password-form',
		constraint: getZodConstraint(ForgotPasswordSchema),
		lastResult: forgotPassword.data,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ForgotPasswordSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<CenteredFormContainer>
			<div>
				<H1 className="text-muted-700 text-center">Forgot Password</H1>
				<P className="text-center">No worries, we'll send you reset instructions.</P>
			</div>
			<forgotPassword.Form method="POST" {...getFormProps(form)}>
				<HoneypotInputs />
				<Flex orientation="vertical" gap="10">
					<FormField
						className="max-w-[320px] min-w-[50%]"
						label={<Label htmlFor="email">Email</Label>}
						input={
							<Input
								aria-invalid={email.errorId ? true : undefined}
								aria-describedby={email.errorId}
								placeholder="Enter your email"
								autoFocus
								autoComplete="email"
								{...getInputProps(email, { type: 'email' })}
							/>
						}
						errors={email.errors}
					/>
					<Button
						variant="primary"
						size="md"
						/* status={forgotPassword.state === 'submitting' ? 'pending' : forgotPassword.data?.status ?? 'idle'} */
						type="submit"
						isDisabled={forgotPassword.state !== 'idle'}
					>
						Recover password
					</Button>
					<P className="text-center">
						<A href="/login" prefetch="intent">
							Back to Login
						</A>
					</P>
				</Flex>
			</forgotPassword.Form>
		</CenteredFormContainer>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
